// src/lib/permissions/hooks/usePermissions.ts
// Clean, modern React hooks for permission management

import { useMemo } from 'react';
import { usePermissionStore } from '../store/permissionStore';
import { ModuleCategory, AccessLevel } from '../core/modules';

/**
 * Primary permission hook
 * Single source for all permission needs
 */
export const usePermissions = () => {
  const store = usePermissionStore();
  
  // Memoize permission API for performance
  const api = useMemo(() => ({
    // Core permission checks
    hasPermission: store.hasPermission,
    hasAnyPermission: store.hasAnyPermission,
    hasAllPermissions: store.hasAllPermissions,
    
    // Module access
    canAccessModule: store.canAccessModule,
    canViewModule: store.canViewModule,
    canUpdateModule: store.canUpdateModule,
    getModuleAccessLevel: store.getModuleAccessLevel,
    
    // Admin access
    hasAdminAccess: store.hasAdminAccess,
    
    // Module information
    getAccessibleModules: store.getAccessibleModules,
    getModulesByCategory: store.getModulesByCategory,
    getAllModules: store.getAllModules,
    
    // Advanced features
    getPermissionStats: store.getPermissionStats,
    validatePermissions: store.validatePermissions,
    getAccessSummary: store.getAccessSummary,
    getCacheStats: store.getCacheStats,
    clearCache: store.clearCache,
  }), [store]);
  // Memoize UI helpers for common use cases
  const ui = useMemo(() => ({
    // Navigation helpers
    navigation: {
      showAdminMenu: api.hasAdminAccess(),
      showDashboard: api.canAccessModule('dashboard'),
      showLanes: api.canAccessModule('lanes'),
      showQueues: api.canAccessModule('queues'),
      showCalendar: api.canAccessModule('calendar'),
      showPowerBI: api.hasPermission('admin_bi_dashboard_access'),
      show2DVisualization: api.hasPermission('2d_visualisation_access'),
      show3DVisualization: api.hasPermission('3d_visualisation_access'),
    },
    
    // Dashboard tile helpers
    dashboard: {
      showTerminalTile: api.hasPermission('dashboard_terminal-tile_view'),
      showCheckpointTile: api.hasPermission('dashboard_checkpoint-tile_view'),
      showQueueTile: api.hasPermission('dashboard_queue-tile_view'),
      showLaneTile: api.hasPermission('dashboard_lane-tile_view'),
      canUpdateCheckpoints: api.hasPermission('dashboard_checkpoint_status_update'),
    },
    
    // Button helpers
    buttons: {
      editCalendar: api.hasPermission('calendar_update_access'),
      editCheckpoints: api.hasPermission('admin_checkpoint_update_access'),
      editLanes: api.hasPermission('lane_status_update'),
      editQueues: api.hasPermission('queue_status_update'),
    },
    
    // Page access helpers
    pages: {
      canAccessAdminDashboard: api.hasAdminAccess(),
      canAccessLanesPage: api.canAccessModule('lanes'),
      canAccessQueuesPage: api.canAccessModule('queues'),
      canAccessCalendarPage: api.canAccessModule('calendar'),
    }
  }), [api]);

  return {
    // Core API
    ...api,
    
    // State information
    permissions: store.permissions,
    userGroups: store.userGroups,
    currentUser: store.currentUser,
    loading: store.loading,
    error: store.error,
    noGroups: store.noGroups,
    environment: store.environment,
    loadTimestamp: store.loadTimestamp,    
    // Actions
    setPermissions: store.setPermissions,
    setUserGroups: store.setUserGroups,
    setCurrentUser: store.setCurrentUser,
    setLoading: store.setLoading,
    setError: store.setError,
    setNoGroups: store.setNoGroups,
    setEnvironmentInfo: store.setEnvironmentInfo,
    clearError: store.clearError,
    reset: store.reset,
    getUserGroup: store.getUserGroup,
    
    // UI helpers
    ui
  };
};

/**
 * Hook for specific permission checking
 * Optimized for single permission checks
 */
export const usePermissionCheck = (permission: string) => {
  const { hasPermission, loading, error } = usePermissions();
  
  return useMemo(() => ({
    hasAccess: hasPermission(permission),
    loading,
    error
  }), [permission, hasPermission, loading, error]);
};

/**
 * Hook for multiple permission checking
 * Supports any/all logic
 */
export const usePermissionChecks = (
  permissions: string[], 
  mode: 'any' | 'all' = 'any'
) => {
  const { hasAnyPermission, hasAllPermissions, loading, error } = usePermissions();
  
  return useMemo(() => ({
    hasAccess: mode === 'any' 
      ? hasAnyPermission(permissions)
      : hasAllPermissions(permissions),
    loading,
    error
  }), [permissions, mode, hasAnyPermission, hasAllPermissions, loading, error]);
};
/**
 * Hook for module access checking
 * Provides comprehensive module access information
 */
export const useModuleAccess = (moduleName: string) => {
  const { 
    canAccessModule, 
    canViewModule, 
    canUpdateModule, 
    getModuleAccessLevel,
    loading,
    error 
  } = usePermissions();
  
  return useMemo(() => ({
    canAccess: canAccessModule(moduleName),
    canView: canViewModule(moduleName),
    canUpdate: canUpdateModule(moduleName),
    accessLevel: getModuleAccessLevel(moduleName),
    loading,
    error
  }), [
    moduleName, 
    canAccessModule, 
    canViewModule, 
    canUpdateModule, 
    getModuleAccessLevel,
    loading,
    error
  ]);
};

/**
 * Hook for admin access checking
 * Specialized for admin functionality
 */
export const useAdminAccess = () => {
  const { hasAdminAccess, getModulesByCategory, loading, error } = usePermissions();
  
  return useMemo(() => ({
    hasAccess: hasAdminAccess(),
    adminModules: getModulesByCategory('admin'),
    loading,
    error
  }), [hasAdminAccess, getModulesByCategory, loading, error]);
};

/**
 * Hook for permission statistics
 * Useful for debugging and admin dashboards
 */
export const usePermissionStats = () => {
  const { getPermissionStats, getCacheStats, validatePermissions } = usePermissions();
  
  return useMemo(() => ({
    permissionStats: getPermissionStats(),
    cacheStats: getCacheStats(),
    validation: validatePermissions()
  }), [getPermissionStats, getCacheStats, validatePermissions]);
};